import apiClient from './apiClient';
import { API_ENDPOINTS, TIMEOUTS } from '../constants/api';

/** In-memory cache so StrictMode remounts / duplicate effects share one HTTP call */
const listCache = {
  templates: null,
  airliners: null
};
const listInflight = {
  templates: null,
  airliners: null
};

function fetchWithListCache(key, fetcher) {
  if (listCache[key]) {
    return Promise.resolve(listCache[key]);
  }
  if (listInflight[key]) {
    return listInflight[key];
  }

  listInflight[key] = fetcher()
    .then((result) => {
      listCache[key] = result;
      listInflight[key] = null;
      return result;
    })
    .catch((err) => {
      listInflight[key] = null;
      throw err;
    });

  return listInflight[key];
}

export function invalidateContractTemplatesCache() {
  listCache.templates = null;
  listInflight.templates = null;
}

export function invalidateContractAirlinersCache() {
  listCache.airliners = null;
  listInflight.airliners = null;
}

const contractService = {
  /**
   * Fetch all contracts with pagination, search, and sorting
   * @param {string} queryParams - URL query parameters (e.g., "page=1&limit=10&search=test")
   * @returns {Promise} Response data with contracts and pagination
   */
  listAll: async (queryParams = '') => {
    const url = queryParams ? `${API_ENDPOINTS.CONTRACTS.LIST}?${queryParams}` : API_ENDPOINTS.CONTRACTS.LIST;
    return await apiClient.get(url);
  },

  /**
   * Fetch all airliners for dropdown
   * @returns {Promise} Response data with airliners array
   */
  getAirliners: async () => {
    return fetchWithListCache('airliners', () =>
      apiClient.get(`${API_ENDPOINTS.CONTRACTS.LIST}/airliners`)
    );
  },

  /**
   * Create a new contract and trigger HelloSign
   * @param {Object} data - Contract data
   * @returns {Promise} Response data
   */
  create: async (data) => {
    return await apiClient.post(API_ENDPOINTS.CONTRACTS.CREATE, data);
  },

  /**
   * Get contract details and signing status
   * @param {number|string} id - Contract ID
   * @returns {Promise} Response data
   */
  getDetail: async (id) => {
    return await apiClient.get(
      `${API_ENDPOINTS.CONTRACTS.DETAIL}/${id}`,
      {},
      { timeout: TIMEOUTS.LONG_RUNNING }
    );
  },

  /**
   * Live Dropbox Sign signature status (signer table). Separate slow call.
   * @param {number|string} id - Contract ID
   * @returns {Promise}
   */
  getSignatureStatus: async (id) => {
    return await apiClient.get(
      `${API_ENDPOINTS.CONTRACTS.SIGNATURE_STATUS}/${id}/signature-status`,
      {},
      { timeout: TIMEOUTS.LONG_RUNNING }
    );
  },

  /**
   * Update contract metadata/remark
   * @param {number|string} id - Contract ID
   * @param {Object} data - Update data
   * @returns {Promise} Response data
   */
  update: async (id, data) => {
    return await apiClient.put(`${API_ENDPOINTS.CONTRACTS.UPDATE}/${id}`, data);
  },

  /**
   * Soft delete a contract
   * @param {number|string} id - Contract ID
   * @returns {Promise} Response data
   */
  delete: async (id) => {
    return await apiClient.delete(`${API_ENDPOINTS.CONTRACTS.DELETE}/${id}`);
  },

  /**
   * Restore a soft-deleted contract
   * @param {number|string} id - Contract ID
   * @returns {Promise} Response data
   */
  restore: async (id) => {
    return await apiClient.post(`${API_ENDPOINTS.CONTRACTS.RESTORE}/${id}/restore`);
  },

  /**
   * Permanently delete a contract (hard delete)
   * @param {number|string} id - Contract ID
   * @returns {Promise} Response data
   */
  permanentDelete: async (id) => {
    return await apiClient.delete(`${API_ENDPOINTS.CONTRACTS.DELETE}/${id}/permanent`);
  },

  /**
   * Get HelloSign PDF download URL
   * @param {number|string} id - Contract ID
   * @returns {Promise} Response data with fileUrl
   */
  download: async (id) => {
    return await apiClient.get(
      `${API_ENDPOINTS.CONTRACTS.DOWNLOAD}/${id}/download`,
      {},
      { timeout: TIMEOUTS.LONG_RUNNING }
    );
  },

  /**
   * Send signature reminder to a specific signer
   * @param {number|string} id - Contract ID
   * @param {string} email - Signer's email
   * @returns {Promise} Response data
   */
  remind: async (id, email) => {
    return await apiClient.post(`${API_ENDPOINTS.CONTRACTS.REMIND}/${id}/remind`, { email });
  },

  /**
   * Void contract and invalidate active signature request.
   * @param {number|string} id - Contract ID
   * @param {string} reason - Optional reason
   * @returns {Promise} Response data
   */
  voidContract: async (id, reason = '') => {
    return await apiClient.post(`${API_ENDPOINTS.CONTRACTS.VOID}/${id}/void`, { reason });
  },

  /**
   * Download Excel report
   * @returns {Promise} Blob for the excel file
   */
  exportExcel: async () => {
    // For direct file downloads, it's often better to use window.open or a specific link
    // but if it needs auth, we use apiClient with responseType 'blob'
    const token = sessionStorage.getItem('token');
    const url = `${apiClient.baseURL}${API_ENDPOINTS.CONTRACTS.EXPORT_EXCEL}`;

    // We can use fetch directly to handle the blob
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    if (!response.ok) throw new Error('Failed to download Excel report');
    return await response.blob();
  },

  /**
   * Fetch all HelloSign templates
   * @returns {Promise} Response data with templates array
   */
  getTemplates: async () => {
    return fetchWithListCache('templates', () =>
      apiClient.get(API_ENDPOINTS.CONTRACTS.TEMPLATES, {}, { timeout: null })
    );
  },

  /**
   * Upload PDF and create an embedded template draft in HelloSign.
   * @param {FormData} formData - template_file, title, optional subject/message
   * @returns {Promise}
   */
  uploadTemplate: async (formData) => {
    const response = await apiClient.upload(API_ENDPOINTS.CONTRACTS.TEMPLATE_UPLOAD, formData);
    invalidateContractTemplatesCache();
    return response;
  }
};

export default contractService;